<template>
  <div>
    <div class="create-target-form__name-section upload">
      <v-row justify="space-around">
        <v-col md="6">
          <target-name
            :isNewTarget="isNewTarget"
            :targetType="target.target || defaultTargetType"
            :initialValue="(initialTarget || { display_name: '' }).display_name"
            :disabled="wasActivated"
            :validateAllNames="!showActivationButton"
            @updateName="val => (target.display_name = val)"
            @updateNameIsValid="isValid => (nameIsValid = isValid)"
          />
        </v-col>
      </v-row>
    </div>
    <div class="create-target-form__source-label xandr-create-target__source-label">File Type</div>

    <div class="create-target-form__source-icons xandr-create-target__source-icons">
      <div
        class="target-form-upload__destination"
        v-for="fileType in filteredFileTypes"
        :key="fileType.id"
      >
        <div v-if="!shouldHideOption(fileType)">
          <img
            class="create-target-form__mri-def target-form-upload__destination-icon create-target-form__data-icon--def"
            :src="getDestinationImage(fileType)"
            @click="selectDestination(fileType)"
          />
          <div class="target-form-upload__destination-title">{{ fileType.title }}</div>
        </div>
      </div>
    </div>
    <template v-if="!isXandrUpload">
      <div class="target-form-upload__options-area">
        <v-row justify="space-around">
          <v-col md="4">
            <advertiser-select
              class="vue-select"
              ref="advertiserSelect"
              placeholder="Select"
              :initialValue="target.advertiser"
              @update="val => (target.advertiser = val)"
              :disabled="wasActivated"
            ></advertiser-select>
          </v-col>
          <v-col md="4">
            <streaming-select
              :initialValue="target.viewing_stream"
              @input="onViewingStreamChange"
              :viewingOption="viewingOptions"
              ref="viewingStreamSelect"
            />
          </v-col>
        </v-row>
      </div>

      <template v-if="!hadStagedFile">
        <div
          class="target-form-upload__options-area"
          style="margin-top: 5px; display: block"
        >
          <v-row justify="space-around">
            <v-col
              md="4"
              class="d-flex justify-center align-center"
            >
              <v-file-input
                :accept="acceptedFileFormat"
                ref="fileUploadInput"
                :disabled="isUploadDisabled || comscoreSelection != null"
                @change="onFileChange"
                @click:clear="clearSelectedFile"
                v-model="uploadedFile"
                placeholder="No file chosen"
                prepend-icon="mdi-microsoft-excel"
              >
              </v-file-input>
            </v-col>
            <v-col
              md="4"
              class="d-flex justify-center align-center"
            >
              <div
                v-if="isComscoreDataSource"
                class="target-form-upload__select-area"
              >
                <div
                  class="evenly-distributed__target-form-dropdown-select"
                  v-bind:class="{ 'absolute-input': this.comscoreSelection != null }"
                >
                  <comscore-select
                    @update="onComscoreChange"
                    class="vue-select"
                    ref="comscoreSelect"
                    :disabled="hadStagedFile || selectedFiles != null"
                  />
                </div>
              </div>
              <div
                v-else-if="isPIDDataSource"
                class="target-form-upload__select-area"
              >
                <broadcast-quarter-select @input="val => (broadcastQuarter = val)" />
              </div>
            </v-col>
          </v-row>
          <v-row
            v-if="isPIDDataSource"
            justify="space-around"
            style="margin-top: 0"
          >
            <v-col
              md="4"
              class="d-flex justify-center align-center"
            >
              <!-- placeholder for alignment -->
            </v-col>
            <v-col
              md="4"
              class="d-flex"
            >
              <demo-qualifier @demoQualifier="val => (demoQualifier = val)" />
            </v-col>
          </v-row>
        </div>
      </template>
      <div class="target-form-upload__button-area">
        <v-row justify="space-around">
          <v-col
            md="3"
            class="d-flex justify-center align-center"
          >
            <action-button
              v-if="showActivationButton"
              label="activate"
              id="uploadModalActivateBtn"
              color="secondary"
              :disabled="disableActivationButton"
              @click.native="clickedActivate"
            >
            </action-button>
          </v-col>
          <v-col
            md="3"
            class="d-flex justify-center align-center"
          >
            <action-button
              label="send downstream"
              id="uploadModalSendDownstreamBtn"
              color="secondary"
              :disabled="!uploadButtonClickable"
              @click.native="clickedSubmit"
            >
            </action-button>
          </v-col>
        </v-row>
      </div>
      <error-modal
        ref="errorModal"
        :message="errorMessage"
      ></error-modal>
    </template>
  </div>
</template>

<script>
  import { cloneDeep } from 'lodash'
  import ActionButton from '@/components/common/actionButton'
  import { ErrorModal } from '@/components/common/feedbackModal'
  import AdvertiserSelect from '@/appComponents/targets/components/AdvertiserSelect'
  import TargetName from '../components/TargetName'
  import StreamingSelect from '@/appComponents/targets/components/StreamingSelect'
  import ComscoreSelect from '@/appComponents/targets/components/ComscoreSelect'
  import BroadcastQuarterSelect from '@/appComponents/targets/components/BroadcastQuarterSelect'
  import DemoQualifier from '@/appComponents/targets/components/DemoQualifier'
  import {
    DATASOURCES,
    DESTINATIONS,
    TARGET_TYPE,
    TARGET_TYPES_INTEGERS,
    ATT_STATUS,
    ACTIVATION_STATUS,
    VIEWING_STREAMS,
  } from '@/appLogic/targets/Constants'
  import { validatePidFile } from '@/appLogic/targets/pid/PidLogic'
  import Toast from '@/components/common/toasts'
  import { NumberInput } from '@/components/common/inputs'
  import SvgIcon from '@/components/common/svgIcon'
  import cancel from '@/assets/svg/cancel.svg'

  import PIDDefaultImg from '@/assets/fileTypes/PID-Default.png'
  import PIDSelectedImg from '@/assets/fileTypes/PID-Selected.png'
  import NPowerDefaultImg from '@/assets/fileTypes/NPower-Default.png'
  import NPowerSelectedImg from '@/assets/fileTypes/NPower-Selected.png'
  import XandrDefaultImg from '@/assets/dataSources/Xandr-Default.png'
  import XandrSelectedImg from '@/assets/dataSources/Xandr-Selected.png'
  import ComscoreDefaultImg from '@/assets/fileTypes/Comscore-Default.png'
  import ComscoreSelectedImg from '@/assets/fileTypes/Comscore-Selected.png'

  const FILE_TYPE_OBJECTS = [
    {
      id: DATASOURCES.PID,
      title: 'PID',
      target: TARGET_TYPE.PID,
      targetType: TARGET_TYPES_INTEGERS.PID,
      img: {
        default: PIDDefaultImg,
        selected: PIDSelectedImg,
      },
      isDisabled: false,
    },
    {
      id: DATASOURCES.NIELSEN,
      title: 'NPower',
      target: TARGET_TYPE.NPOWER,
      targetType: TARGET_TYPES_INTEGERS.NPOWER,
      img: {
        default: NPowerDefaultImg,
        selected: NPowerSelectedImg,
      },
      isDisabled: false,
    },
    {
      id: DATASOURCES.ATTDIRECTTV,
      title: 'Xandr',
      target: TARGET_TYPE.ATT,
      targetType: TARGET_TYPES_INTEGERS.ATT,
      img: {
        default: XandrDefaultImg,
        selected: XandrSelectedImg,
      },
      isDisabled: false,
    },
    {
      id: DATASOURCES.COMSCORE,
      title: 'Comscore',
      target: TARGET_TYPE.COMSCORE,
      targetType: TARGET_TYPES_INTEGERS.COMSCORE,
      img: {
        default: ComscoreDefaultImg,
        selected: ComscoreSelectedImg,
      },
      isDisabled: false,
    },
  ]

  export default {
    components: {
      ActionButton,
      AdvertiserSelect,
      StreamingSelect,
      ComscoreSelect,
      BroadcastQuarterSelect,
      DemoQualifier,
      ErrorModal,
      TargetName,
      NumberInput,
      SvgIcon,
    },
    directives: {
      focus,
    },
    props: {
      onComplete: {
        type: Function,
        default: () => {},
      },
      initialTarget: {
        type: Object,
        default: null,
      },
      errorMessage: {
        type: String,
        default: '',
      },
      originalTarget: {
        type: Object,
        default: null,
      },
    },
    data() {
      const newTarget = this.initialTarget ? cloneDeep(this.initialTarget) : {}
      let target = newTarget
      target.target_datasource = newTarget.target_datasource || null
      target.am_data = newTarget.am_data || {}
      target.am_data.flightStartDate = newTarget.am_data.flightStartDate || null
      target.am_data.flightEndDate = newTarget.am_data.flightEndDate || null
      target.am_data.status = newTarget.am_data.status || null
      target.am_data.xdFlightOverlap = newTarget.am_data.xdFlightOverlap || 0
      target.destinations = [DESTINATIONS.SYMPHONY]

      return {
        // We clone the target so we don't affect the original data until the form is submitted
        target,
        selectedFiles: null,
        uploadedFile: [],
        nameIsValid: null,
        fileTypes: FILE_TYPE_OBJECTS,
        isXandrUpload: false,
        isIrisUpload: false,
        comscoreSelection: null,
        cancel,
        broadcastQuarter: null,
        demoQualifier: null,
      }
    },
    computed: {
      acceptedFileFormat() {
        if (this.target.target === TARGET_TYPE.NPOWER || this.target.target === TARGET_TYPE.COMSCORE) {
          return '.xlsx,.xls'
        } else if (this.target.target === TARGET_TYPE.PID) {
          return '.csv'
        }
      },
      filteredFileTypes() {
        return this.fileTypes.filter(f => f.title !== 'Xandr')
      },
      isNewTarget() {
        return !this.target.id
      },
      uploadButtonClickable() {
        if (!this.$store.getters.hasPublishPermissions) {
          return false
        }
        if (this.isComscoreDataSource && this.comscoreSelection != null) {
          return this.haveReceivedValues
        }
        if (!this.hadStagedFile && this.selectedFiles === null) {
          return false
        }
        if (this.isPIDDataSource && (this.broadcastQuarter === null || this.demoQualifier === null)) {
          return false
        }
        return this.haveReceivedValues
      },
      activateButtonClickable() {
        if (this.isComscoreDataSource && this.comscoreSelection != null) {
          return this.haveReceivedValues
        }
        return false
      },
      haveReceivedValues() {
        return this.target.advertiser && this.nameIsValid && this.target.target_datasource && this.target.viewing_stream
      },
      isUploadDisabled() {
        return !this.target.target
      },
      defaultTargetType() {
        return TARGET_TYPE.LAS
      },
      disableActivationButton() {
        const hasRequiredFields =
          this.target.advertiser && this.target.viewing_stream && this.nameIsValid && this.target.target
        const isPIDAndQuarterSelected = this.isPIDDataSource && this.broadcastQuarter && this.demoQualifier
        return (
          !hasRequiredFields ||
          this.wasActivated ||
          !isPIDAndQuarterSelected ||
          !this.$store.getters.hasPublishPermissions
        )
      },
      wasActivated() {
        return Boolean((this.initialTarget || {}).activation_status)
      },
      hadStagedFile() {
        return Boolean((this.initialTarget || {}).has_staged_file)
      },
      viewingOptions() {
        return this.target.target === TARGET_TYPE.PID ? 3 : 4
      },
      showActivationButton() {
        return (
          this.target.target_datasource &&
          !(this.isNeilsenDatasource || this.isComscoreDataSource) &&
          !this.hadStagedFile &&
          !this.wasActivated
        )
      },
      isNeilsenDatasource() {
        return this.target.target_datasource === DATASOURCES.NIELSEN
      },
      isComscoreDataSource() {
        return this.target.target_datasource === DATASOURCES.COMSCORE
      },
      isPIDDataSource() {
        return this.target.target_datasource === DATASOURCES.PID
      },
      clearBtnVisibility() {
        return this.selectedFiles ? 'visible' : 'hidden'
      },
    },
    methods: {
      clearSelectedFile() {
        if (!this.isXandrUpload) {
          if (this.$refs.fileUploadInput) {
            this.$refs.fileUploadInput.reset()
          }
        }
        this.selectedFiles = null
      },
      clickedActivate() {
        if (this.target.target === TARGET_TYPE.PID) {
          this.target.target_definition = { broadcastQuarter: this.broadcastQuarter }
          this.target.metadata.foresite = { demoQualifier: this.demoQualifier }
        }
        this.$emit('activate', {
          target: this.target,
          file: this.selectedFiles ? this.selectedFiles[0] : null,
        })
      },
      getDestinationImage(fileType) {
        return this.target.target_datasource === fileType.id && !fileType.isDisabled
          ? fileType.img.selected
          : fileType.img.default
      },
      shouldHideOption(fileType) {
        return fileType.id === DATASOURCES.ATTDIRECTTV && this.isNewTarget
      },
      selectDestination(fileType) {
        if (!this.wasActivated) {
          this.clearSelectedFile()
          this.onComscoreChange()
          if (this.target.target_datasource === fileType.id) {
            this.target.target_datasource = null
            this.target.target_type = null
            this.target.target = null
            this.target.am_data.status = null
          } else {
            this.target.target_datasource = fileType.id
            this.target.target_type = fileType.targetType
            this.target.target = fileType.target
            this.target.am_data.status = fileType.id === DATASOURCES.ATTDIRECTTV ? ATT_STATUS.PLANNING : null
          }
        }
      },
      clickedSubmit() {
        if (this.feedbackMessage) {
          this.$refs.errorModal.open()
        } else {
          if (this.target.target === TARGET_TYPE.PID) {
            this.target.activation_status = ACTIVATION_STATUS.LIVE
            this.target.target_definition = { broadcastQuarter: this.broadcastQuarter }
            let foresiteMetadata = this.target.metadata.foresite || {}
            this.target.metadata.foresite = { ...foresiteMetadata, demoQualifier: this.demoQualifier }
          }
          if (!this.isIrisUpload) {
            this.onComplete()
          }
          this.$emit('submit', {
            target: this.target,
            file: this.hadStagedFile || !this.selectedFiles ? null : this.selectedFiles[0],
          })
        }
      },
      onFileChange(event) {
        if (event instanceof File) {
          if (this.target.target === TARGET_TYPE.NPOWER || this.target.target === TARGET_TYPE.COMSCORE) {
            this.selectedFiles = [event]
          } else {
            this.selectedFiles = null
            validatePidFile(event, valid => {
              if (valid) {
                this.selectedFiles = [event]
              } else {
                Toast.error({ title: 'Error has occurred', message: `Error: File has invalid values` })
              }
            })
          }
        }
      },
      onViewingStreamChange(val) {
        this.target.viewing_stream = val
      },
      theinputrecieved(val) {
        this.target = val
      },
      onComscoreChange(val) {
        if (val != null) {
          this.target.target_definition = { target: `${val.value.demo_no}` }
          this.target.audience_size = `${val.value.national_demo_universe}`
          this.comscoreSelection = val
          this.clearSelectedFile()
        } else {
          this.target.target_definition = null
          this.comscoreSelection = null
        }
      },
    },
    watch: {
      initialTarget(newTarget) {
        this.target = cloneDeep(newTarget)
      },
      'target.target_datasource': function () {
        this.isXandrUpload = this.target.target_datasource === DATASOURCES.ATTDIRECTTV
        this.isIrisUpload =
          this.target.target_datasource === DATASOURCES.NIELSEN ||
          this.target.target_datasource === DATASOURCES.COMSCORE
        if (this.isPIDDataSource) {
          this.target.viewing_stream = VIEWING_STREAMS.C3
        } else {
          this.broadcastQuarter = null
          this.demoQualifier = null
        }
      },
    },
  }
</script>

<style lang="scss">
  .target-form-upload {
    &__options-area {
      display: flex;
      margin: 20px 0;
    }

    &__ue-area {
      width: 100%;
      text-align: center;
      margin-top: 20px;
    }

    &__date-area {
      margin-top: 20px;
      margin-bottom: 15px;
      width: 100%;
    }

    &__select-area {
      flex: 1;

      & + div {
        margin-left: 10px;
      }
    }

    &__button-area {
      margin: 30px 0 0;
      display: flex;
      flex-direction: row;
    }

    &__upload-input {
      margin-top: 15px;
      width: 100%;
      display: flex;
      justify-content: center;
    }

    &__clear-btn {
      align-self: flex-end;
      display: flex;
      width: 34px;
      height: 34px;
      align-items: center;
      justify-content: center;
      cursor: pointer;

      svg {
        fill: black;
      }
    }

    &__att-status {
      margin-top: 15px;
      width: 100%;
      text-align: center;
    }

    &__upload-file {
      display: flex;
      flex-direction: column;
      widows: 100%;
      align-items: center;
    }

    &__upload-select-option {
      margin-top: 15px;
    }

    &__destination {
      display: flex;
      flex-direction: column;
    }
    &__destination-icon {
      height: 76px;
      margin-left: 10px;
    }
    &__destination-title {
      text-align: center;
      margin-left: 10px;
      font-weight: bold;
    }
  }
  .create-target-form__activate_button {
    float: left;
    margin-left: 80px;
  }
  .create-target-form__submit_button {
    float: right;
    margin-right: 80px;
  }
  .upload-input-custom {
    margin-top: 25px;
    width: 35%;
    display: flex;
    justify-content: left;
  }
  .v-select .dropdown-toggle {
    overflow-x: hidden;
    overflow: hidden;
    white-space: nowrap !important;
  }
  .absolute-input {
    input[type='search'] {
      position: absolute;
    }
  }
</style>



// WEBPACK FOOTER //
// TargetFormUpload.vue?383166e7